/**
 * Mixpanel Event Tracking Functions
 * 
 * This file contains all event tracking functions following the new event schema.
 * Each event follows the pattern: ScreenID_ActionID
 * 
 * Event Schema:
 * - Screen ID: letter.number.number-screen-name
 * - Action ID: describes what the user did
 * - Event ID: ScreenID_ActionID
 * 
 * Event Categories:
 * 1. Page Views (4 total)
 * 2. CTA Events (5 total) 
 * 3. External Link Events (4 total)
 */

import { analytics } from './config';

/**
 * PAGE VIEW EVENTS
 * Track when users view different pages of the portfolio
 */

export const trackPageView = (screenId, pageName, additionalProps = {}) => {
  analytics.track(`${screenId}_view`, {
    screen_id: screenId,
    action_id: 'view',
    page_name: pageName,
    referrer: document.referrer ? new URL(document.referrer).origin : 'direct',
    ...additionalProps,
  });
};

// Page view tracking functions
export const trackHomePageView = () => {
  trackPageView('a.1.0-home', 'Portfolio Home', {
    section: 'home',
    page_type: 'main_portfolio',
  });
};

export const trackGuestSpeakerPageView = () => {
  trackPageView('b.1.0-guest-speaker', 'Guest Speaker Event', {
    section: 'guest-speaker',
    page_type: 'event_details',
  });
};

export const trackAcademicRecognitionPageView = () => {
  trackPageView('c.1.0-academic-recognition', 'Academic Recognition', {
    section: 'academic-recognition',
    page_type: 'achievement_details',
  });
};

export const trackCaseStudyPageView = () => {
  trackPageView('d.1.0-case-study', 'Delivery System Case Study', {
    section: 'case-study',
    page_type: 'case_study_details',
  });
};

/**
 * CTA EVENTS (Call-to-Action)
 * Track "Book a Call" CTAs from different sections
 */

export const trackCTAClick = (screenId, sectionName, additionalProps = {}) => {
  analytics.track(`${screenId}_book-call`, {
    screen_id: screenId,
    action_id: 'book-call',
    section: sectionName,
    cta_type: 'book_strategy_call',
    cta_url: 'https://form.typeform.com/to/SLoI0dkA',
    ...additionalProps,
  });
};

// CTA tracking functions
export const trackHeroCTAClick = () => {
  trackCTAClick('a.1.0-home', 'hero', {
    cta_position: 'primary_hero',
    cta_text: 'Book a Strategy Call',
  });
};

export const trackProblemsCTAClick = () => {
  trackCTAClick('a.2.0-problems', 'problems', {
    cta_position: 'problems_section',
    cta_text: 'Book a Strategy Call',
  });
};

export const trackStrengthsCTAClick = () => {
  trackCTAClick('a.3.0-strengths', 'strengths', {
    cta_position: 'founder_strengths',
    cta_text: 'Book a Strategy Call',
  });
};

export const trackQuizCTAClick = () => {
  trackCTAClick('a.4.0-quiz', 'quiz', {
    cta_position: 'quiz_completion',
    cta_text: 'Book a Strategy Call',
  });
};

export const trackCaseStudyCTAClick = () => {
  trackCTAClick('d.1.0-case-study', 'case-study', {
    cta_position: 'case_study_takeaway',
    cta_text: 'Book a Strategy Call',
  });
};

/**
 * EXTERNAL LINK EVENTS
 * Track clicks to external websites and resources
 */

export const trackExternalLinkClick = (linkType, url, sectionName, additionalProps = {}) => {
  analytics.track(`external_${linkType}-click`, {
    screen_id: 'external',
    action_id: `${linkType}-click`,
    section: sectionName,
    external_url: url,
    link_type: linkType,
    ...additionalProps,
  });
};

// External link tracking functions
export const trackElemento43Click = (sectionName, url) => {
  trackExternalLinkClick('elemento43', url, sectionName, {
    company_name: 'Elemento43',
    link_category: 'company_website',
  });
};

export const trackLinkedInClick = (sectionName, url) => {
  trackExternalLinkClick('linkedin', url, sectionName, {
    platform: 'LinkedIn',
    link_category: 'professional_profile',
  });
};

export const trackRecognitionPDFClick = (sectionName, url) => {
  trackExternalLinkClick('recognition-pdf', url, sectionName, {
    document_type: 'recognition_certificate',
    link_category: 'document_download',
  });
};

export const trackCertificationClick = (sectionName, url, certificationName) => {
  trackExternalLinkClick('certification', url, sectionName, {
    certification_name: certificationName,
    link_category: 'certification_badge',
  });
};

/**
 * CONTACT INTERACTION EVENTS
 * Track contact method clicks (email, phone)
 */

export const trackContactEmailClick = (email, sectionName) => {
  analytics.track('contact_email-click', {
    action_id: 'email-click',
    section: sectionName,
    contact_type: 'email',
    email_address: email,
  });
};

export const trackContactPhoneClick = (phone, sectionName) => {
  analytics.track('contact_phone-click', {
    action_id: 'phone-click',
    section: sectionName,
    contact_type: 'phone',
    phone_number: phone,
  });
};

/**
 * SESSION TRACKING
 * Track user session information
 */

export const trackSessionStart = () => {
  analytics.track('session_started', {
    action_id: 'session-start',
    language: navigator.language,
    platform: navigator.platform,
    cookie_enabled: navigator.cookieEnabled,
    online: navigator.onLine,
    session_type: 'page_load',
  });
};

/**
 * CASE STUDY SPECIFIC EVENTS
 * Track interactions within case studies
 */

export const trackCaseStudyCardClick = (caseStudyTitle, sectionName = 'case-studies') => {
  analytics.track('a.5.0-case-studies_card-click', {
    screen_id: 'a.5.0-case-studies',
    action_id: 'card-click',
    section: sectionName,
    case_study_title: caseStudyTitle,
    interaction_type: 'navigation_to_case_study',
  });
};

// Export all tracking functions for easy importing
export const mixpanelEvents = {
  // Page views
  trackHomePageView,
  trackGuestSpeakerPageView,
  trackAcademicRecognitionPageView,
  trackCaseStudyPageView,
  
  // CTAs
  trackHeroCTAClick,
  trackProblemsCTAClick,
  trackStrengthsCTAClick,
  trackQuizCTAClick,
  trackCaseStudyCTAClick,
  
  // External links
  trackElemento43Click,
  trackLinkedInClick,
  trackRecognitionPDFClick,
  trackCertificationClick,
  
  // Contact interactions
  trackContactEmailClick,
  trackContactPhoneClick,
  
  // Session
  trackSessionStart,
  
  // Case studies
  trackCaseStudyCardClick,
};

export default mixpanelEvents;